// Shared types for WeeklyRotaDetail — shape of the API response from GETRotaByDateEndPoint

export interface MentorAbsenceType {
    absenceEndDate: Date;
    absenceStartDate: Date;
    mentorId: Guid;
    rotaId: Guid;
    id: Guid;
}

export interface MentorStudentRotaEntry {
    id: Guid;
    studentStatusId: Guid;
    firstName: string;
    lastName: string;
    mentorStudentRotaId: Guid;
    studentSessionId: Guid;
    sessionStartTime: string | null;
    sessionEndTime: string | null;
    reportIncident: boolean;
    didAttend: boolean;
    sessionStatusId: string;
    sessionStatusName: string;
    monAMStudentSessionId: Guid | null;
    monPMStudentSessionId: Guid | null;
    tuesAMStudentSessionId: Guid | null;
    tuesPMStudentSessionId: Guid | null;
    wedsAMStudentSessionId: Guid | null;
    wedsPMStudentSessionId: Guid | null;
    thurAMStudentSessionId: Guid | null;
    thurPMStudentSessionId: Guid | null;
    friAMStudentSessionId: Guid | null;
    friPMStudentSessionId: Guid | null;
}

export interface MentorAvailability {
    mentorId: string;
    firstName: string;
    lastName: string;
    mentorAvailabilityId: Guid;
    monAM: boolean;
    monPM: boolean;
    tuesAM: boolean;
    tuesPM: boolean;
    wedsAM: boolean;
    wedsPM: boolean;
    thurAM: boolean;
    thurPM: boolean;
    friAM: boolean;
    friPM: boolean;
    monAMOverride: boolean;
    monPMOverride: boolean;
    tuesAMOverride: boolean;
    tuesPMOverride: boolean;
    wedsAMOverride: boolean;
    wedsPMOverride: boolean;
    thurAMOverride: boolean;
    thurPMOverride: boolean;
    friAMOverride: boolean;
    friPMOverride: boolean;
    mentorStudentRota: MentorStudentRotaEntry[];
    mentorRotaAbsences: MentorAbsenceType[];
}

/** Discriminates the mutually-exclusive action area of a cell. */
export const CellActionMode = {
    CrossIcon: "crossIcon",
    OverTime: "overTime",
    UnavailableDelete: "unavailableDelete",
    Session: "session",
} as const;

export type CellActionMode = typeof CellActionMode[keyof typeof CellActionMode];

/** All derived display state for a single schedule cell, computed by DomainManager.getCellState(). */
export interface CellRenderState {
    background: string;
    /** Determines which action area variant to render. Mutually exclusive by construction. */
    actionMode: CellActionMode;
    /** Flattened session fields — view never navigates into a session object directly. */
    studentName: string;
    sessionStatusText: string;
    studentSessionId: Guid | null;
    mentorStudentRotaId: Guid | null;
    displayStart: string;
    displayEnd: string;
    /** Pre-formatted time range string, e.g. "(9:00 am - 1:00 pm)". Empty when times are absent. */
    formattedTimeRange: string;
    sessionStatusColour: string;
    attendanceColour: string;
    attendanceText: string;
    incidentText: string;
    /** Rendered unconditionally before the action area. */
    showEditUnavailableAssigned: boolean;
    showRemoveDetailsButton: boolean;
    /** Derived action-mode flags — one will be true, the rest false. */
    showCrossIconAction: boolean;
    showOverTimeAction: boolean;
    showUnavailableDeleteAction: boolean;
    showSessionContent: boolean;
    /** Sub-flags for the "session" action mode. */
    showLiveModeStatus: boolean;
    showStudentNameLink: boolean;
    showTimes: boolean;
    showAddStudentButton: boolean;
    showEditDeleteButton: boolean;
}

/** Resolved data for a single schedule cell. */
export interface CellSessionData {
    studentName: string;
    studentId: Guid;
    isStudentSessionAvailable: boolean;
    isMentorAbsenceOverride: boolean;
    mentorStudentRotaId: Guid;
    studentSessionId: Guid;
    sessionStartTime: string | null;
    sessionEndTime: string | null;
    reportIncident: boolean;
    didAttend: boolean | null;
    sessionStatusId: string;
    sessionStatusName: string;
}
